import { apiFetch } from '../lib/apiFetch';

export interface PrivateEventHistoryChange {
  /** The stored field key: 'headcount', 'dealValueCents', 'location', 'crew', … */
  field: string;
  before: unknown;
  after: unknown;
}

export interface PrivateEventHistoryEntry {
  id: string;
  /** 'created' | 'edited' | 'cancelled' | 'imported' | 'import_updated', or a future value. */
  eventType: string;
  actorName: string;
  /** ISO instant. */
  changedAt: string;
  changes: PrivateEventHistoryChange[];
}

/** A person or type the server resolved from an id. */
export interface NamedRef {
  id: string;
  name: string;
}

export interface HistoryAddress {
  locationName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
}

/** Where an event was, as recorded when the change was written. */
export interface HistoryLocation {
  kind: 'venue' | 'client_site' | 'tbd' | 'unrecorded';
  venueId: string | null;
  regionId: string | null;
  label: string;
  /** Only a client site has one. Rows written before it existed carry no key. */
  address?: HistoryAddress | null;
}

/** A 403: the caller may open the event but not read its change history. */
export class PrivateEventHistoryForbiddenError extends Error {
  constructor() {
    super('No access to the change history');
    this.name = 'PrivateEventHistoryForbiddenError';
  }
}

export async function fetchPrivateEventHistory(id: string): Promise<PrivateEventHistoryEntry[]> {
  const res = await apiFetch(`/api/v1/private-events/${id}/history`);
  if (res.status === 403) throw new PrivateEventHistoryForbiddenError();
  if (!res.ok) throw new Error(`Failed to fetch change history: ${res.status}`);
  const data = (await res.json()) as { entries: PrivateEventHistoryEntry[] };
  return data.entries;
}
